import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import CustomStore from 'devextreme/data/custom_store';
import { addWeeks, format } from 'date-fns';
import { useHistory } from 'react-router-dom';

import api from '../../../services/api';
import { errorMessage$, successMessage$ } from '../../../utils/subjects';
import InputInlineEdit from '../../../components/InputInlineEdit';
import SelectInlineEdit from '../../../components/SelectInlineEdit';
import SoldValue from './SoldValue';
import PendingProjectCosts from './PendingProjectCosts';

// Mesmo teto da api (EXTENSION_WEEKS.max).
const MAX_WEEKS_TO_ADD = 104;

function isValidWeeksToAdd(value) {
  const number = Number(value);
  return value !== '' && Number.isInteger(number) && number >= 1 && number <= MAX_WEEKS_TO_ADD;
}

function formatAmount(value) {
  return value === null || value === undefined
    ? '-'
    : Number(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
}

function currencyStore() {
  return {
    store: new CustomStore({
      key: 'ID',
      loadMode: 'raw',
      load: async () => {
        const response = await api.get('master/currencies');
        return response.data.map(currency => ({
          ...currency,
          DisplayName: `${currency.Name} - ${currency.Abbreviation}`,
        }));
      },
    }),
    paginate: true,
  };
}

// Busca do projeto de destino. Guarda o que já carregou: o SelectInlineEdit só
// devolve o id escolhido, e é daqui que vêm código, cliente e status dele.
function targetStore(initialTarget, cache) {
  if (initialTarget) cache.set(initialTarget.ID, initialTarget);

  return new CustomStore({
    key: 'ID',
    load: async loadOptions => {
      const search = loadOptions.searchValue || '';
      if (search.length < 2) return initialTarget ? [initialTarget] : [];
      const response = await api.get('scheduling/project-orders/projects', {
        params: { search },
      });
      (response.data || []).forEach(project => cache.set(project.ID, project));
      return response.data;
    },
    byKey: async key => cache.get(key) || null,
  });
}

// Modo "Attach to existing" do painel: o staffing confirma o projeto de destino e,
// se for o caso, o value adjustment. Nada é gravado no destino antes do Attach.
export default function PendingProjectAttach({
  detail,
  save,
  saving,
  readOnly,
  handleClose,
}) {
  const history = useHistory();
  const [target, setTarget] = useState(detail.TargetProject);
  const [targetCache] = useState(() => new Map());
  const [targetSource] = useState(() =>
    targetStore(detail.TargetProject, targetCache),
  );
  const [currencySource] = useState(currencyStore);
  const [addAdjustment, setAddAdjustment] = useState(
    detail.Adjustment.SuggestedOn && detail.Adjustment.Amount !== null,
  );
  const [attaching, setAttaching] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [attachWarnings, setAttachWarnings] = useState(null);

  // Semanas no fim do destino: vem com as semanas do cenário vendido (Weeks, do
  // TotalWeeks do inclient) e o staffing ajusta aqui mesmo — um campo só, sem data
  // de início nem segundo número para desencontrar.
  const [addWeeksOn, setAddWeeksOn] = useState(!!detail.IsExtension);
  const [weeksToAdd, setWeeksToAdd] = useState(detail.Weeks ? String(detail.Weeks) : '');
  const weeksValid = isValidWeeksToAdd(weeksToAdd);

  const salesCode = detail.SalesProjectCode;
  const codeDiffers = !!target && !!salesCode && target.ProjectCode !== salesCode;
  const adjustmentAvailable =
    detail.Adjustment.Amount !== null && !!detail.Adjustment.CurrencyId;

  const handleTargetChange = useCallback(
    ({ IDProject }) => setTarget(IDProject ? targetCache.get(IDProject) || null : null),
    [targetCache],
  );

  const attach = useCallback(
    async attachReason => {
      setAttaching(true);
      try {
        const response = await api.post(`scheduling/project-orders/${detail.ID}/attach`, {
          IDProject: target.ID,
          AddAdjustment: addAdjustment,
          AddWeeks: addWeeksOn && weeksValid ? Number(weeksToAdd) : null,
          Reason: attachReason || null,
        });
        const { IDProject, Warnings } = response.data;

        if (Warnings && Warnings.length > 0) {
          setAttachWarnings({ projectId: IDProject, messages: Warnings });
          return;
        }

        successMessage$.next({ open: true, message: 'Order attached to the project' });
        history.push(`/projects/${IDProject}/allocations`);
      } catch (err) {
        if (err.response && err.response.status === 409) {
          errorMessage$.next({ open: true, message: err.response.data.message });
          handleClose();
        }
        setAttaching(false);
      }
    },
    [
      detail.ID,
      target,
      addAdjustment,
      addWeeksOn,
      weeksValid,
      weeksToAdd,
      history,
      handleClose,
    ],
  );

  return (
    <>
      <div className="project-info-box">
        <SelectInlineEdit
          value={target ? target.ID : null}
          displayValue={target ? target.DisplayName : ''}
          dataField="IDProject"
          caption="Target project"
          displayExpr="DisplayName"
          valueExpr="ID"
          dataSource={targetSource}
          onChange={handleTargetChange}
        />
      </div>
      {target && (
        <Typography variant="body2" color="textSecondary" style={{ margin: '0 12px 8px' }}>
          {[target.ClientName, target.Status].filter(Boolean).join(' · ')}
        </Typography>
      )}
      {codeDiffers && (
        <Alert severity="warning" style={{ margin: 5 }}>
          Sales registered <strong>{salesCode}</strong>. The order will be linked to{' '}
          <strong>{target.ProjectCode}</strong> and sales will be informed.
        </Alert>
      )}

      {/* Só "adicionar no fim", como o Add Week da alocação — é a única forma de
          somar semanas que o sistema oferece. */}
      <Box className="project-info-box" style={{ display: 'block' }}>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={addWeeksOn}
              disabled={!target}
              onChange={e => setAddWeeksOn(e.target.checked)}
            />
          }
          label="Add weeks at the end of the target project"
        />
        {addWeeksOn && (
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <TextField
              type="number"
              size="small"
              label="Weeks to add"
              value={weeksToAdd}
              error={!weeksValid}
              inputProps={{ min: 1, max: MAX_WEEKS_TO_ADD }}
              style={{ width: 120 }}
              onChange={e => setWeeksToAdd(e.target.value)}
            />
            <Typography variant="body2" color="textSecondary">
              {!weeksValid && 'Fill the weeks to add.'}
              {weeksValid &&
                target &&
                target.EndDate &&
                `Ends ${format(new Date(target.EndDate), 'dd MMM yyyy')} → ${format(
                  addWeeks(new Date(target.EndDate), Number(weeksToAdd)),
                  'dd MMM yyyy',
                )} (+${weeksToAdd} weeks)`}
            </Typography>
          </Box>
        )}
      </Box>

      <Box className="project-info-box" style={{ display: 'block' }}>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={addAdjustment}
              disabled={!adjustmentAvailable}
              onChange={e => setAddAdjustment(e.target.checked)}
            />
          }
          label="Add a value adjustment to the target project"
        />
        <Typography variant="body2" color="textSecondary">
          {detail.Adjustment.SellValueType} · {detail.Adjustment.Operation} ·{' '}
          {formatAmount(detail.Adjustment.Amount)}{' '}
          {detail.Adjustment.CurrencyName || ''}
          {detail.Adjustment.MainAmount !== null &&
            ` · USD ${formatAmount(detail.Adjustment.MainAmount)}`}
        </Typography>
        {!adjustmentAvailable && (
          <Typography variant="body2" color="textSecondary">
            Fill the amount and currency below to add the adjustment.
          </Typography>
        )}
      </Box>

      {/* Valor local, moeda e USD que o staffing confere — sem despesa embutida.
          Vêm do inclient e vão para o ajuste como estiverem aqui. */}
      <div className="project-info-box">
        <InputInlineEdit
          type="number"
          value={detail.ProjectValueInitial ?? ''}
          displayValue={
            <SoldValue
              amount={detail.ProjectValueInitial}
              currency={detail.Names.CurrencyAbbreviation}
              mainAmount={detail.ProjectValueInitialMain}
            />
          }
          dataField="ProjectValueInitial"
          caption="Adjustment amount (net)"
          onChange={save}
        />
      </div>
      <div className="project-info-box">
        <SelectInlineEdit
          value={detail.IDCurrency}
          displayValue={detail.Names.Currency}
          dataField="IDCurrency"
          caption="Adjustment currency"
          displayExpr="DisplayName"
          valueExpr="ID"
          dataSource={currencySource}
          onChange={save}
        />
      </div>

      <PendingProjectCosts costs={detail.AdditionalCosts} />

      <Box mt={2} display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
        <Button variant="outlined" onClick={handleClose} disabled={attaching}>
          Keep pending
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            readOnly || attaching || saving || !target || (addWeeksOn && !weeksValid)
          }
          onClick={() => (codeDiffers ? setReasonOpen(true) : attach(null))}
        >
          {attaching ? <CircularProgress size={22} color="inherit" /> : 'Attach'}
        </Button>
      </Box>

      <Dialog open={reasonOpen} onClose={() => setReasonOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Project code differs from sales</DialogTitle>
        <DialogContent>
          <Alert severity="warning" style={{ marginBottom: 16 }}>
            Sales registered <strong>{salesCode}</strong>. The order will be linked to{' '}
            <strong>{target?.ProjectCode}</strong>, and sales will be informed.
          </Alert>
          <TextField
            label="Reason"
            required
            fullWidth
            multiline
            rows={3}
            value={reason}
            inputProps={{ maxLength: 500 }}
            onChange={e => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!reason.trim() || attaching}
            onClick={() => {
              setReasonOpen(false);
              attach(reason.trim());
            }}
          >
            Attach to this project
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!attachWarnings} maxWidth="sm" fullWidth>
        <DialogTitle>Order attached, with warnings</DialogTitle>
        <DialogContent>
          {attachWarnings &&
            attachWarnings.messages.map(message => (
              <Alert key={message} severity="warning" style={{ marginBottom: 8 }}>
                {message}
              </Alert>
            ))}
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={() =>
              history.push(`/projects/${attachWarnings.projectId}/allocations`)
            }
          >
            Open the project
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
